'use client';

import React, { useCallback, useRef, useMemo, useEffect, useLayoutEffect, useState } from 'react';
import { DEFAULT_BEST_LINE_MIN_LIQUIDITY } from '@sharpr/pricing';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  ChevronLeft,
  ChevronRight,
  X,
  Search,
  Filter,
  Users,
  Clock,
  Construction,
  RotateCcw
} from 'lucide-react';
import { cn, DEFAULT_LIQUIDITY_THRESHOLDS, TIER_DOT_CLASS, type LiquidityThresholds } from '@sharpr/ui';
import { SelectablePlayerCard } from './SelectablePlayerCard';
import { MarketFilterLocal } from './MarketFilterLocal';
import { TeamFilterLocal } from './TeamFilterLocal';
import type { QuestionablePlayer } from '@/hooks/useNbaBoard';
import { QuestionableFilter } from './QuestionableFilter';
import { getBestEvForPlayer } from '../../utils/bestLineCalculator';
import { useDebounce } from '../../hooks/useDebounce';
import { useMlbWeather } from '../../hooks/useMlbWeather';
import { useSport } from '../../contexts/SportContext';
import { useSportSeasons } from '../../hooks/useSportSeasons';
import { seasonStartBoundary } from '../../services/billing/seasonClamp';
import { useOptionalTour } from './tour/TourContext';
import type { SelectablePlayer } from '../../types/selection';
import type { MarketCode } from '@sharpr/types';
import type { OverlayTarget, ResearchTarget } from '../../types/research';
import { teamNames, fullTeamNames, teamCities } from '../../constants/teamNames';
import { useAnchoredRow } from '@/hooks/useAnchoredRow';

// A card's key: its player and game (a doubleheader player has TWO cards sharing an id).
const cardKey = (p: { id: string; game?: { id?: string } }) => `${p.id}:${p.game?.id ?? 'g'}`;

// useLayoutEffect on the client (correct scroll before paint, no flash),
// useEffect on the server to avoid the SSR warning.
const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

// Count badge on a collapsed-rail icon (filter count, selected-player count).
const COLLAPSED_BADGE_CLASS =
  'absolute -top-1 -right-1 w-5 h-5 bg-brand-500 dark:bg-brand-600 text-white text-xs rounded-full flex items-center justify-center font-medium';

// Hover tooltip shown to the right of a collapsed-rail icon.
function CollapsedTooltip({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div
      className={cn(
        'absolute left-full ml-2 px-2 py-1 bg-gray-900 dark:bg-bg-quaternary text-white dark:text-text-primary text-xs rounded',
        'opacity-0 group-hover:opacity-100 transition-opacity duration-200',
        'pointer-events-none whitespace-nowrap z-50',
        'top-1/2 -translate-y-1/2'
      )}
    >
      {children}
      <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900 dark:border-r-bg-quaternary" />
    </div>
  );
}

// Format a 24h "HH:MM" string into a 12h clock label, e.g. "13:05" -> "1:05 PM".
export function formatAvailabilityTime(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

// Format a "YYYY-MM-DD" calendar date as a short label, e.g. "2026-10-21" -> "Oct 21".
function formatSeasonStartDate(ymd: string): string {
  const [y, mo, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, mo - 1, d)).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', timeZone: 'UTC',
  });
}

// The NBA season's start date while it is still in the future, else null.
// Malformed/missing calendar data degrades to null (time-only label).
function upcomingSeasonStartDate(startDate: string | undefined): string | null {
  if (!startDate) return null;
  try {
    return Date.now() < seasonStartBoundary(startDate).getTime() ? startDate : null;
  } catch {
    return null;
  }
}

function useFormattedTimestamp(timestamp?: string): string {
  const [formatted, setFormatted] = useState('');

  useEffect(() => {
    if (!timestamp) { setFormatted(''); return; }

    const update = () => {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);

      if (diffMins < 1) { setFormatted('Updated just now'); return; }
      if (diffMins < 60) { setFormatted(`Updated ${diffMins}m ago`); return; }

      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) { setFormatted(`Updated ${diffHours}h ago`); return; }

      setFormatted(`Updated ${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`);
    };

    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [timestamp]);

  return formatted;
}

// The panel renders from a selection-INDEPENDENT player array; each card's `isSelected`
// is derived from `selectedIds` at render time. Baking isSelected into the array (the
// old `players` prop) changed its identity on every toggle, busting the filter/sort memo
// and forcing every visible card to re-render.
type PanelPlayer = Omit<SelectablePlayer, 'isSelected'>;

interface PlayerSelectionPanelProps {
  players: PanelPlayer[];
  selectedIds: Set<string>;
  isCollapsed: boolean;
  onTogglePlayer: (id: string) => void;
  onToggleCollapse: () => void;
  onSelectAll: (playerIds: string[]) => void;
  onClearAll: () => void;
  selectedMarkets: Set<MarketCode>;
  onMarketsChange: (markets: Set<MarketCode>) => void;
  selectedTeams: Set<string>;
  onTeamsChange: (teams: Set<string>) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  lastUpdated?: string;
  download?: React.ReactNode;   // the board's Download menu, beside the filter button (absent: none)
  projectionsHidden?: boolean;
  projectionsAvailableAt?: string;
  selectedSportsbooks: string[];
  /** NBA questionable players (the Conditionals chips), those the viewer has out (boardPlayerKey) and their handlers. */
  questionablePlayers?: QuestionablePlayer[];
  outQuestionable?: ReadonlySet<string>;
  onToggleOut?: (key: string) => void;
  onClearOut?: () => void;
  onOpenResearch?: (target: ResearchTarget) => void;
  /** Open the line-movement overlay for a clicked line/cell. Owned by
   *  PlayerSelectionView so the mobile panel and the desktop table share
   *  one overlay mount and the share-slip deep-link can drive it. */
  onLineMovementClick?: (target: OverlayTarget) => void;
  minEVThreshold?: number;
  onMinEVThresholdChange?: (value: number) => void;
  liquidityThresholds?: LiquidityThresholds;
  onLiquidityThresholdsChange?: (value: LiquidityThresholds) => void;
  /** USD: an exchange side with less liquidity never wins a best line (it still shows in its book's column). */
  minBestLineLiquidity?: number;
  onMinBestLineLiquidityChange?: (value: number) => void;
  confirmedLineupsOnly: boolean;
  onConfirmedLineupsOnlyChange: (next: boolean) => void;
  className?: string;
  /** Extra class(es) for every player card's virtual-item wrapper (e.g. demo fade-in hook). */
  cardClassName?: string;
  /** Disable the MLB weather fetch — frozen demo slate never matches live weather. */
  weatherEnabled?: boolean;
}

const NO_QUESTIONABLE: QuestionablePlayer[] = [];

export function PlayerSelectionPanel({
  players,
  selectedIds,
  isCollapsed,
  onTogglePlayer,
  onToggleCollapse,
  onSelectAll,
  onClearAll,
  selectedMarkets,
  onMarketsChange,
  selectedTeams,
  onTeamsChange,
  searchQuery,
  onSearchChange,
  lastUpdated,
  download,
  projectionsHidden,
  projectionsAvailableAt,
  selectedSportsbooks,
  questionablePlayers = NO_QUESTIONABLE,
  outQuestionable,
  onToggleOut,
  onClearOut,
  onOpenResearch,
  onLineMovementClick,
  minEVThreshold = 0,
  onMinEVThresholdChange,
  liquidityThresholds,
  onLiquidityThresholdsChange,
  minBestLineLiquidity,
  onMinBestLineLiquidityChange,
  confirmedLineupsOnly,
  onConfirmedLineupsOnlyChange,
  className,
  cardClassName,
  weatherEnabled = true
}: PlayerSelectionPanelProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  const debouncedSearchQuery = useDebounce(searchQuery, 200);
  const formattedTimestamp = useFormattedTimestamp(lastUpdated);
  const sport = useSport();
  const { getWeather } = useMlbWeather(weatherEnabled && sport === 'mlb');
  const seasons = useSportSeasons();

  // Projections availability text (replaces timestamp when hidden). Before the NBA
  // season opens, the availability day is the season start, so name it alongside the time.
  const getProjectionsTimestampText = (): string => {
    if (!projectionsHidden) return formattedTimestamp;
    const seasonStart = sport === 'nba'
      ? upcomingSeasonStartDate(seasons.find((s) => s.sport === 'nba')?.start_date)
      : null;
    const dateLabel = seasonStart ? formatSeasonStartDate(seasonStart) : null;
    if (!projectionsAvailableAt) return dateLabel ? `Available ${dateLabel}` : 'Coming soon';
    const timeLabel = `${formatAvailabilityTime(projectionsAvailableAt)} ET`;
    return dateLabel ? `Available ${dateLabel} at ${timeLabel}` : `Available at ${timeLabel}`;
  };
  const projectionsTimestampText = getProjectionsTimestampText();
  const TimestampIcon = projectionsHidden ? Construction : Clock;
  const timestampIconColor = projectionsHidden
    ? 'text-amber-500 dark:text-amber-400'
    : 'text-gray-400 dark:text-text-tertiary';
  const timestampTextColor = projectionsHidden
    ? 'text-amber-600 dark:text-amber-400'
    : 'text-muted-foreground dark:text-text-tertiary';

  // Mobile card expansion state (accordion-style, single card at a time)
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);

  // Teams/Markets disclosure state, lifted from FilterShell. Both start open
  // (chips visible); collapsing BOTH switches the headers to the space-saving
  // side-by-side row, and opening either reverts to full-width stacking (the
  // chips need the whole panel width).
  const [teamsExpanded, setTeamsExpanded] = useState(true);
  const [marketsExpanded, setMarketsExpanded] = useState(true);
  const bothFiltersCollapsed = !teamsExpanded && !marketsExpanded;
  // The side-by-side collapse (Teams | Markets on one line) only fits when
  // Markets is a single row — i.e. NBA. MLB splits Markets into Pitcher/Batter
  // rows, too tall to share the line, so MLB always stacks.
  const sideBySide = bothFiltersCollapsed && sport !== 'mlb';

  // Session-only filter popover (Min-EV + liquidity tier cutoffs).
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersRef = useRef<HTMLDivElement>(null);
  // Top-level Teams + Markets filter block. Lives outside `parentRef` (the list
  // scroller), so the card click-outside handler must whitelist it — otherwise
  // tapping a market/team chip while a card is expanded would collapse the card.
  const filterChipsRef = useRef<HTMLDivElement>(null);

  // Walkthrough: while the tour is on the filter step, force the popover open
  // (so the user sees what clicking the button reveals) and suppress
  // click-outside dismissal so the spotlight backdrop doesn't slam it shut.
  const tour = useOptionalTour();
  const tourWantsFilterOpen = tour?.active === true && tour.step?.id === 'tour-filter-popover';
  useEffect(() => {
    if (tour?.active) setFiltersOpen(tourWantsFilterOpen);
  }, [tour?.active, tourWantsFilterOpen]);

  // Walkthrough: the mobile line-movement steps need a specific card expanded
  // so the per-book breakdown (and its `tour-demo-first-book` target) is in
  // the DOM. Match openLineMovementDemo's pick — first player with a
  // bestMarket whose books list is non-empty — so the expanded card holds
  // the same lines the chart overlay will render.
  const demoPlayerId = useMemo(() => {
    const hasOdds = (p: PanelPlayer) =>
      Object.values(p.markets).some((m) => (m.books?.length ?? 0) > 0);
    // Prefer a player with a bestMarket so the pick matches the desktop chart
    // demo. bestMarket is stripped for signed-out / odds-only viewers, though,
    // so fall back to the first player with any odds — the mobile expansion
    // renders per-book lines without EV. Without the fallback the signed-out
    // mobile tour had no card to expand (and no `tour-demo-first-book` target).
    return (
      players.find(
        (p) => p.bestMarket && (p.markets[p.bestMarket.market]?.books?.length ?? 0) > 0,
      )?.id ??
      players.find(hasOdds)?.id ??
      null
    );
  }, [players]);

  // MLB-only walkthrough targets: the first player whose card actually shows a
  // lineup chip / weather pill, so the spotlight lands on a real element rather
  // than a player that happens to lack that data. Null (no match on the slate)
  // → the step degrades to a floating explainer with no highlight.
  const demoLineupPlayerId = useMemo(() => {
    if (sport !== 'mlb') return null;
    return players.find(
      (p) => p.lineup || Object.values(p.markets).some((m) => m?.lineup),
    )?.id ?? null;
  }, [players, sport]);
  const demoWeatherPlayerId = useMemo(() => {
    if (sport !== 'mlb') return null;
    return players.find((p) => {
      if (!p.game) return false;
      const w = getWeather(p.game.isHome ? p.team : p.game.opp, p.game.commenceTime);
      return !!w && !w.indoorVenue;
    })?.id ?? null;
  }, [players, sport, getWeather]);
  const tourWantsCardExpanded =
    tour?.active === true &&
    (tour.step?.id === 'line-movement-expand' ||
      tour.step?.id === 'line-movement' ||
      tour.step?.id === 'line-movement-locked' ||
      tour.step?.id === 'line-movement-signed-out');
  useEffect(() => {
    if (!tour) return;
    return tour.registerDemoHandlers({
      expandLineMovementDemoCard: () => {
        if (demoPlayerId) setExpandedCardId(demoPlayerId);
      },
      collapseDemoCards: () => setExpandedCardId(null),
    });
  }, [tour, demoPlayerId]);

  useEffect(() => {
    if (!filtersOpen) return;
    if (tourWantsFilterOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (filtersRef.current && !filtersRef.current.contains(e.target as Node)) {
        setFiltersOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [filtersOpen, tourWantsFilterOpen]);

  const liqOrange = liquidityThresholds?.orange ?? DEFAULT_LIQUIDITY_THRESHOLDS.orange;
  const liqYellow = liquidityThresholds?.yellow ?? DEFAULT_LIQUIDITY_THRESHOLDS.yellow;
  const liqGreen = liquidityThresholds?.green ?? DEFAULT_LIQUIDITY_THRESHOLDS.green;
  const liquidityError = !(liqOrange <= liqYellow && liqYellow <= liqGreen)
    ? 'Cutoffs must satisfy red ≤ orange ≤ yellow'
    : null;

  // Count of distinct active filter *groups* (min-EV, liquidity) — drives the
  // popover header badge and the filter-button dot.
  const evActive = (minEVThreshold ?? 0) > 0;
  const liquidityActive =
    (liquidityThresholds !== undefined &&
      (liquidityThresholds.orange !== DEFAULT_LIQUIDITY_THRESHOLDS.orange ||
        liquidityThresholds.yellow !== DEFAULT_LIQUIDITY_THRESHOLDS.yellow ||
        liquidityThresholds.green !== DEFAULT_LIQUIDITY_THRESHOLDS.green)) ||
    (minBestLineLiquidity !== undefined && minBestLineLiquidity !== DEFAULT_BEST_LINE_MIN_LIQUIDITY);
  const activeFilterCount = (evActive ? 1 : 0) + (liquidityActive ? 1 : 0);
  const filtersActive = activeFilterCount > 0;

  // Restore both session filters to their defaults in one click.
  const resetSessionFilters = useCallback(() => {
    onMinEVThresholdChange?.(0);
    onLiquidityThresholdsChange?.(DEFAULT_LIQUIDITY_THRESHOLDS);
    onMinBestLineLiquidityChange?.(DEFAULT_BEST_LINE_MIN_LIQUIDITY);
  }, [onMinEVThresholdChange, onLiquidityThresholdsChange, onMinBestLineLiquidityChange]);

  // Min-EV slider position on the 0–15% scale — drives both the input value and
  // the gradient-fill percentage (`--range-pct`) on the styled track.
  const minEvSliderValue = Math.min(15, Math.max(0, (minEVThreshold ?? 0) * 100));
  const minEvFillPct = (minEvSliderValue / 15) * 100;

  // Handle card expansion toggle. Stable so React.memo on SelectablePlayerCard
  // doesn't bail on every panel render (~10-20 visible rows × every parent
  // re-render = unnecessary card re-renders).
  const handleToggleExpand = useCallback((playerId: string) => {
    setExpandedCardId(prev => prev === playerId ? null : playerId);
  }, []);

  // Click-outside handler to collapse expanded card. Suppressed while the tour
  // is on a line-movement step that explicitly expanded a card for the demo —
  // the tour overlay/popover sit outside `parentRef` so any click on them
  // would otherwise slam it shut.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tourWantsCardExpanded) return;
      const target = event.target as Node;
      // Interacting with the panel's own filter controls (Teams/Markets chips or
      // the advanced-filters popover) must not collapse an expanded card.
      if (filterChipsRef.current?.contains(target)) return;
      if (filtersRef.current?.contains(target)) return;
      if (expandedCardId && parentRef.current && !parentRef.current.contains(target)) {
        setExpandedCardId(null);
      }
    };

    if (expandedCardId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }

    return undefined;
  }, [expandedCardId, tourWantsCardExpanded]);

  const availableTeams = useMemo(() => {
    const earliestByTeam = new Map<string, string>();
    for (const player of players) {
      if (!player.team) continue;
      const t = player.game?.commenceTime ?? '';
      const current = earliestByTeam.get(player.team);
      if (current === undefined || t < current) {
        earliestByTeam.set(player.team, t);
      }
    }
    return Array.from(earliestByTeam.entries())
      .sort(([aTeam, aTime], [bTeam, bTime]) =>
        aTime === bTime ? aTeam.localeCompare(bTeam) : aTime.localeCompare(bTime))
      .map(([team]) => team);
  }, [players]);

  // Extract unique markets that have odds or projections
  const availableMarkets = useMemo(() => {
    const markets = new Set<MarketCode>();
    players.forEach(player => {
      for (const [m, data] of Object.entries(player.markets)) {
        if (data.projection != null || (data.books && data.books.some(b => !b.isRemoved))) {
          markets.add(m as MarketCode);
        }
      }
    });
    return markets;
  }, [players]);

  // The questionable players, only the filtered teams' when a team filter is on.
  const shownQuestionable = useMemo(
    () => (selectedTeams.size === 0 ? questionablePlayers : questionablePlayers.filter(q => selectedTeams.has(q.teamAbbr))),
    [questionablePlayers, selectedTeams],
  );
  // Teams with questionable players (the amber team-chip dot), from every game — not narrowed by the team filter.
  const teamsWithQuestionable = useMemo(() => new Set(questionablePlayers.map(q => q.teamAbbr)), [questionablePlayers]);

  // Clear selected teams that are no longer available
  // Only run when players are loaded (availableTeams populated) to prevent
  // clearing persisted filters before data loads
  useEffect(() => {
    if (selectedTeams.size > 0 && availableTeams.length > 0) {
      const availableTeamsSet = new Set(availableTeams);
      const stillValidTeams = Array.from(selectedTeams).filter(team =>
        availableTeamsSet.has(team)
      );

      // Only update if some teams were removed
      if (stillValidTeams.length !== selectedTeams.size) {
        onTeamsChange(new Set(stillValidTeams));
      }
    }
  }, [availableTeams, selectedTeams, onTeamsChange]);

  // Filter and sort players - optimized single pass with cached EV calculation.
  // Also returns `structuralCount` — players passing every gate EXCEPT the
  // min-EV floor — so the popover can show "X of Y shown" feedback isolating
  // the EV slider's effect.
  const { list: filteredPlayers, structuralCount } = useMemo(() => {
    const hasMarketFilter = selectedMarkets.size > 0;
    const hasTeamFilter = selectedTeams.size > 0;
    const query = debouncedSearchQuery?.toLowerCase();
    const marketPredicate = hasMarketFilter
      ? (code: MarketCode) => selectedMarkets.has(code)
      : undefined;

    // Helper to check if player matches search query
    const matchesSearch = (player: PanelPlayer): boolean => {
      if (!query) return true;

      // Direct matches (most common case first)
      if (player.name.toLowerCase().includes(query)) return true;
      if (player.team.toLowerCase().includes(query)) return true;
      if (player.position?.toLowerCase().includes(query)) return true;
      if (player.game?.opp?.toLowerCase().includes(query)) return true;

      // Check full team names and cities
      const teamName = teamNames[player.team]?.toLowerCase();
      const fullTeamName = fullTeamNames[player.team]?.toLowerCase();
      const cityName = teamCities[player.team]?.toLowerCase();

      if (teamName?.includes(query) || fullTeamName?.includes(query) || cityName?.includes(query)) {
        return true;
      }

      // Check opponent full names and cities
      if (player.game?.opp) {
        const oppTeamName = teamNames[player.game.opp]?.toLowerCase();
        const oppFullTeamName = fullTeamNames[player.game.opp]?.toLowerCase();
        const oppCityName = teamCities[player.game.opp]?.toLowerCase();

        if (oppTeamName?.includes(query) || oppFullTeamName?.includes(query) || oppCityName?.includes(query)) {
          return true;
        }
      }

      return false;
    };

    // Helper to check if player has any matching markets with actual data (projection or books)
    const hasMatchingMarket = (player: PanelPlayer): boolean => {
      if (!hasMarketFilter) return true;
      return Object.entries(player.markets).some(([m, data]) => {
        if (!selectedMarkets.has(m as MarketCode)) return false;
        return data?.projection != null || (data?.books?.length ?? 0) > 0;
      });
    };

    // Single pass: filter and compute EV simultaneously
    const playersWithEV: Array<{ player: PanelPlayer; ev: number }> = [];
    let structuralCount = 0;
    // An expanded (open) card must not be yanked out of the list when the user
    // narrows the Markets filter to a market it doesn't offer. We exempt it from
    // the market filter and pin it to the top (below) so it stays in view; its
    // expansion then renders an empty state for the selected market.
    let exemptedExpandedPlayer: PanelPlayer | null = null;

    const applyMlbConfirmedGate = confirmedLineupsOnly && sport === 'mlb';

    for (const player of players) {
      const isExpandedCard = expandedCardId != null && player.id === expandedCardId;
      // Apply filters in order of most restrictive (team filter typically reduces most)
      if (hasTeamFilter && !selectedTeams.has(player.team)) continue;
      if (!hasMatchingMarket(player)) {
        if (isExpandedCard) exemptedExpandedPlayer = player;
        continue;
      }
      if (!matchesSearch(player)) continue;

      // MLB "confirmed lineups only" gate — structured `lineup.status === 'Confirmed'`
      // when the relevant team has posted its lineup. For TWPs (Ohtani), each role
      // carries its own per-market `lineup`: pitcher markets → opposing lineup,
      // batter markets → own-team lineup. Require ALL present markets to be
      // confirmed so a TWP is hidden until both sides have locked lineups.
      if (applyMlbConfirmedGate) {
        const marketsWithLineup = Object.values(player.markets).filter(m => m?.lineup);
        const passed = marketsWithLineup.length > 0
          ? marketsWithLineup.every(m => m!.lineup!.status === 'Confirmed')
          : player.lineup?.status === 'Confirmed';
        if (!passed) continue;
      }

      // Player passed cheap filters — calculate EV from the same `bestLines`
      // source the card badge and desktop odds-table filter read, then apply
      // min-EV gate. Applies regardless of selection: mobile has no
      // selection UI, and on desktop the parent auto-selects every player on
      // load, which would otherwise make this gate a no-op for the player list.
      // Passed every structural gate (team/market/search/confirmed) — counts
      // toward the "of Y" denominator regardless of the EV floor below.
      structuralCount++;

      const ev = getBestEvForPlayer(player.markets, marketPredicate);
      // `-Infinity` sentinel means no visible/priced line: drop only when user set a positive floor.
      if (minEVThreshold > 0 && ev < minEVThreshold) continue;

      playersWithEV.push({ player, ev });
    }

    // Sort by cached EV (highest first) and extract players
    const sortedList = playersWithEV.sort((a, b) => b.ev - a.ev).map(item => item.player);
    return {
      // Pin an exempted (filter-mismatched) expanded card to the top so it stays
      // in view while its expansion shows an empty state for the active market.
      list: exemptedExpandedPlayer ? [exemptedExpandedPlayer, ...sortedList] : sortedList,
      // The pinned card is shown, so it counts toward the "of Y" denominator —
      // otherwise the "X of Y" counter can show X > Y.
      structuralCount: exemptedExpandedPlayer ? structuralCount + 1 : structuralCount,
    };
  }, [players, debouncedSearchQuery, selectedTeams, selectedMarkets, minEVThreshold, confirmedLineupsOnly, sport, expandedCardId]);

  // Virtualization setup (players already sorted and have isSelected from parent)
  const CARD_HEIGHT_ESTIMATE = 104; // Approximate height including 8px bottom padding

  const { rangeExtractor, onAnchorChange } = useAnchoredRow(key => filteredPlayers.findIndex(p => cardKey(p) === key));
  const rowVirtualizer = useVirtualizer({
    count: filteredPlayers.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => CARD_HEIGHT_ESTIMATE,
    overscan: 3,
    getItemKey: (index) => {
      const player = filteredPlayers[index];
      return player ? cardKey(player) : index;
    },
    rangeExtractor,
  });

  // An expanded card must stay visually pinned when the Markets filter changes.
  // Toggling a market re-sorts/re-pins the list around the card, which otherwise
  // slides it up or down (sometimes out of the viewport entirely). We record the
  // card's offset within the scroll viewport (kept fresh as the user scrolls) and
  // then, after the list re-renders, nudge scrollTop to restore that exact offset
  // — so the card appears fixed in place rather than scrolling.
  const expandedIdRef = useRef(expandedCardId);
  expandedIdRef.current = expandedCardId;

  // Live offset of the expanded card's top relative to the scroll viewport top.
  const anchorOffsetRef = useRef<number | null>(null);
  const measureAnchorOffset = useCallback((): number | null => {
    const container = parentRef.current;
    const id = expandedIdRef.current;
    if (!container || !id) return null;
    const el = container.querySelector<HTMLElement>(`[data-card-id="${CSS.escape(id)}"]`);
    if (!el) return null;
    return el.getBoundingClientRect().top - container.getBoundingClientRect().top;
  }, []);

  // Keep the recorded offset current while the user scrolls or (re)expands a card,
  // so it's the genuine "before" position the moment a filter toggles.
  useEffect(() => {
    const container = parentRef.current;
    if (!container) return;
    const record = () => { anchorOffsetRef.current = measureAnchorOffset(); };
    record();
    container.addEventListener('scroll', record, { passive: true });
    return () => container.removeEventListener('scroll', record);
  }, [measureAnchorOffset, expandedCardId]);

  // On a Markets/Teams change, restore the recorded offset across the re-render AND
  // the virtualizer's async re-measure (which can reposition rows a few frames later).
  useIsomorphicLayoutEffect(() => {
    const container = parentRef.current;
    const expandedId = expandedIdRef.current;
    if (!container || !expandedId) return;
    const target = anchorOffsetRef.current;
    if (target == null) return;
    // The card's new index can land far outside the virtualizer's rendered window
    // (un-filtering grows the list and re-sorts the card deep into it), in which
    // case it's unmounted and unmeasurable. Seed scrollTop from the virtualizer's
    // offset for that index so the card mounts; the DOM-measured correction then
    // converges on the exact recorded offset.
    const cardIndex = filteredPlayers.findIndex(p => p.id === expandedId);
    let frames = 0;
    let raf = requestAnimationFrame(function correct() {
      const current = measureAnchorOffset();
      if (current != null) {
        const delta = current - target;
        if (Math.abs(delta) > 0.5) container.scrollTop += delta;
      } else if (cardIndex >= 0) {
        const offset = rowVirtualizer.getOffsetForIndex(cardIndex, 'start')?.[0];
        if (offset != null) container.scrollTop = offset - target;
      }
      frames += 1;
      if (frames < 10) raf = requestAnimationFrame(correct);
      else anchorOffsetRef.current = measureAnchorOffset();
    });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMarkets, selectedTeams]);

  return (
    <div className={cn(
      "relative h-full flex transition-all duration-300 ease-out flex-shrink-0",
      isCollapsed ? "w-full md:w-20" : "w-full md:w-[22rem]",
      className
    )}>
      {/* Collapse/Expand Handle (Hidden on Mobile) */}
      <button
        onClick={onToggleCollapse}
        className={cn(
          "absolute right-0 top-1/2 -translate-y-1/2 translate-x-full z-30",
          "w-6 h-12 bg-white dark:bg-bg-secondary border border-gray-200 dark:border-border-primary rounded-r-lg border-l-0",
          "hover:bg-gray-50 dark:hover:bg-bg-tertiary transition-colors duration-200",
          "hidden md:flex items-center justify-center shadow-sm dark:shadow-sticky",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:focus-visible:ring-brand-400"
        )}
        aria-label={isCollapsed ? "Expand panel" : "Collapse panel"}
      >
        {isCollapsed ? <ChevronRight className="w-4 h-4 text-gray-400 dark:text-text-secondary" /> : <ChevronLeft className="w-4 h-4 text-gray-400 dark:text-text-secondary" />}
      </button>

      {/* Collapsed State */}
      {isCollapsed && (
        <div className="w-full flex flex-col items-center justify-center gap-4 px-2 py-4">
          {/* Filter Icon - Teams + Markets Count */}
          <div className="group relative flex flex-col items-center">
            <div className="relative">
              <Filter className="w-6 h-6 text-gray-400 dark:text-text-tertiary" />
              {(selectedTeams.size > 0 || selectedMarkets.size > 0) && (
                <span className={COLLAPSED_BADGE_CLASS}>
                  {selectedTeams.size + selectedMarkets.size}
                </span>
              )}
            </div>

            <CollapsedTooltip>
              {selectedTeams.size === 0 && selectedMarkets.size === 0 ? 'No filters' :
               `${selectedTeams.size} team${selectedTeams.size !== 1 ? 's' : ''}, ${selectedMarkets.size} market${selectedMarkets.size !== 1 ? 's' : ''}`}
            </CollapsedTooltip>
          </div>

          {/* Questionable filter (collapsed) */}
          {outQuestionable && onToggleOut && (
            <QuestionableFilter players={shownQuestionable} out={outQuestionable} onToggle={onToggleOut} onClearAll={onClearOut} isCollapsed={true} />
          )}

          {/* Clock Icon - Last Updated Timestamp / Availability */}
          {(lastUpdated || projectionsHidden) && (
            <div className="group relative flex flex-col items-center">
              <TimestampIcon className={cn('w-6 h-6', timestampIconColor)} />
              <CollapsedTooltip>{projectionsTimestampText}</CollapsedTooltip>
            </div>
          )}

          {/* People Icon - Selected Players Count */}
          <div className="group relative flex flex-col items-center">
            <div className="relative">
              <Users className={cn(
                "w-6 h-6",
                selectedIds.size > 0
                  ? "text-brand-600 dark:text-brand-400"
                  : "text-gray-400 dark:text-text-tertiary"
              )} />
              {selectedIds.size > 0 && (
                <span className={COLLAPSED_BADGE_CLASS}>
                  {selectedIds.size}
                </span>
              )}
            </div>

            <CollapsedTooltip>
              {selectedIds.size === 0
                ? 'No players selected'
                : `${selectedIds.size} player${selectedIds.size !== 1 ? 's' : ''} selected`}
            </CollapsedTooltip>
          </div>
        </div>
      )}

      {/* Expanded Panel */}
      {!isCollapsed && (
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <div className="flex-shrink-0 px-2.5 sm:px-4 pt-2 sm:pt-3 pb-1.5 sm:pb-2 w-full">
            {/* Search + Filter popover */}
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 dark:text-text-tertiary" />
                <input
                  type="text"
                  data-search-input
                  value={searchQuery}
                  onChange={(e) => onSearchChange(e.target.value)}
                  placeholder="Search players or teams"
                  className={cn(
                    "w-full pl-8 sm:pl-9 h-7 sm:h-auto sm:py-2 text-xs sm:text-sm leading-none sm:leading-normal",
                    searchQuery ? "pr-9" : "pr-3",
                    "bg-white dark:bg-bg-tertiary text-gray-900 dark:text-text-primary",
                    "border border-gray-200 dark:border-border-primary rounded-lg",
                    "focus:outline-none focus:border-gray-300 dark:focus:border-border-interactive",
                    "placeholder:text-gray-400 dark:placeholder:text-text-tertiary"
                  )}
                />
                {searchQuery && (
                  <button
                    onClick={() => onSearchChange('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-text-tertiary hover:text-gray-400 dark:hover:text-text-secondary transition-colors"
                    aria-label="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Filter icon + popover */}
              {(onMinEVThresholdChange || onLiquidityThresholdsChange) && (
                <div ref={filtersRef} className="relative">
                  <button
                    type="button"
                    data-tour="tour-demo-filter-button"
                    onClick={() => setFiltersOpen(v => !v)}
                    className={cn(
                      // Mobile: labeled pill (icon + "Filters"). Desktop (md+):
                      // collapses back to the compact icon-only circle.
                      // transition-all + active:scale-95 matches the filter chips' tap feedback (FILTER_CHIP_BASE).
                      "relative inline-flex items-center gap-1.5 md:gap-0 px-2.5 md:px-2 py-1.5 md:py-2 rounded-full border transition-all duration-200 active:scale-95 motion-reduce:active:scale-100 text-xs font-medium whitespace-nowrap",
                      filtersActive
                        ? "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/40 hover:bg-blue-500/20"
                        : "bg-white dark:bg-bg-tertiary text-gray-600 dark:text-text-secondary border-gray-200 dark:border-border-primary hover:border-gray-400 dark:hover:border-border-interactive"
                    )}
                    title="Filters"
                    aria-label="Filters"
                    aria-expanded={filtersOpen}
                  >
                    <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                    <span className="md:hidden">Filters</span>
                    {filtersActive && (
                      <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-blue-500 dark:bg-blue-400" />
                    )}
                  </button>

                  {filtersOpen && (
                    <div
                      className={cn(
                        // Bumped above the tour spotlight backdrop (z-[9998])
                        // while the walkthrough wants this popover visible —
                        // otherwise the dim covers it and the user thinks
                        // nothing opened.
                        "absolute right-0 top-full mt-2 w-72 rounded-lg border border-gray-200 dark:border-border-primary bg-white dark:bg-bg-secondary shadow-lg p-3 space-y-3",
                        tourWantsFilterOpen ? "z-[9999]" : "z-40"
                      )}
                    >
                      {/* Header: title + active-count badge + reset */}
                      <div className="flex items-center justify-between pb-2 border-b border-gray-200 dark:border-border-primary/60">
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-700 dark:text-text-secondary">
                          Filters
                          {activeFilterCount > 0 && (
                            <span className="inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-brand-500 dark:bg-brand-600 text-white text-[10px] font-medium tabular-nums">
                              {activeFilterCount}
                            </span>
                          )}
                        </span>
                        <button
                          type="button"
                          onClick={resetSessionFilters}
                          disabled={!filtersActive}
                          className={cn(
                            "inline-flex items-center gap-1 text-[11px] font-medium transition-colors",
                            filtersActive
                              ? "text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 cursor-pointer"
                              : "text-gray-300 dark:text-text-tertiary/40 cursor-default"
                          )}
                        >
                          <RotateCcw className="w-3 h-3" />
                          Reset
                        </button>
                      </div>

                      {/* Min EV */}
                      {onMinEVThresholdChange && (
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between">
                            <label className="text-[11px] font-medium text-muted-foreground dark:text-text-tertiary uppercase tracking-wider">
                              Minimum EV
                            </label>
                            {(minEVThreshold ?? 0) === 0 ? (
                              <span className="text-xs font-medium text-muted-foreground dark:text-text-tertiary">Off</span>
                            ) : (
                              <span className="text-xs font-mono tabular-nums font-semibold text-gray-900 dark:text-text-primary">
                                {((minEVThreshold ?? 0) * 100).toFixed(1)}%
                              </span>
                            )}
                          </div>
                          <input
                            type="range"
                            min={0}
                            max={15}
                            step={0.5}
                            value={minEvSliderValue}
                            onChange={(e) => {
                              const v = parseFloat(e.target.value);
                              onMinEVThresholdChange(Number.isFinite(v) ? Math.max(0, Math.min(15, v)) / 100 : 0);
                            }}
                            aria-label="Minimum EV percentage"
                            className="range-brand"
                            style={{ '--range-pct': `${minEvFillPct}%` } as React.CSSProperties}
                          />
                          {/* Tick scale at common values (5/10), with range ends */}
                          <div className="relative h-3 select-none">
                            <span className="absolute left-0 top-0 text-[9px] leading-none text-muted-foreground dark:text-text-tertiary">0%</span>
                            <span className="absolute top-0 -translate-x-1/2 text-[9px] leading-none text-muted-foreground dark:text-text-tertiary" style={{ left: '33.33%' }}>5</span>
                            <span className="absolute top-0 -translate-x-1/2 text-[9px] leading-none text-muted-foreground dark:text-text-tertiary" style={{ left: '66.67%' }}>10</span>
                            <span className="absolute right-0 top-0 text-[9px] leading-none text-muted-foreground dark:text-text-tertiary">15%</span>
                          </div>
                          <div className="flex items-end justify-between gap-2">
                            <p className="text-[11px] text-muted-foreground dark:text-text-tertiary">
                              Hide players/rows below this edge.
                            </p>
                            <span className="shrink-0 text-[11px] tabular-nums text-gray-500 dark:text-text-tertiary">
                              <span className="font-semibold text-gray-700 dark:text-text-secondary">{filteredPlayers.length}</span>
                              {' of '}{structuralCount}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Liquidity thresholds — one editable row per tier
                          (color → "under $X"); top tier derives from the last
                          cutoff. Dot colors come from TIER_DOT_CLASS so the
                          legend always matches the odds-table dots. */}
                      {onLiquidityThresholdsChange && liquidityThresholds && (
                        <div className="space-y-1.5 pt-2 border-t border-gray-200 dark:border-border-primary/60">
                          <label className="text-[11px] font-medium text-muted-foreground dark:text-text-tertiary uppercase tracking-wider">
                            Exchange Liquidity Tiers
                          </label>
                          <p className="text-[10px] text-muted-foreground dark:text-text-tertiary">
                            Color rows by available bet limit.
                          </p>
                          <div className="space-y-1.5">
                            {([
                              { tier: 'dead', label: 'red', key: 'orange', placeholder: '1' },
                              { tier: 'thin', label: 'orange', key: 'yellow', placeholder: '100' },
                              { tier: 'ok', label: 'yellow', key: 'green', placeholder: '200' },
                            ] as const).map(({ tier, label, key, placeholder }) => (
                              <div key={key} className="flex items-center gap-2">
                                <span className={cn('inline-block w-2 h-2 rounded-full shrink-0', TIER_DOT_CLASS[tier])} />
                                <span className="text-xs capitalize text-gray-700 dark:text-text-secondary">{label}</span>
                                <div className="ml-auto flex items-center gap-1.5">
                                  <span className="text-[11px] text-muted-foreground dark:text-text-tertiary">under</span>
                                  <div className="relative">
                                    <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground dark:text-text-tertiary/60 pointer-events-none">$</span>
                                    <input
                                      type="number"
                                      min={0}
                                      step={1}
                                      placeholder={placeholder}
                                      value={liquidityThresholds[key] === 0 ? '' : liquidityThresholds[key]}
                                      onChange={(e) => {
                                        const raw = e.target.value;
                                        const v = raw === '' ? 0 : parseInt(raw, 10);
                                        onLiquidityThresholdsChange({
                                          ...liquidityThresholds,
                                          [key]: Number.isNaN(v) ? 0 : Math.max(0, v),
                                        });
                                      }}
                                      className="block w-[4.5rem] pl-4 pr-1.5 py-1 text-xs tabular-nums rounded-md border border-gray-200 dark:border-border-primary bg-white dark:bg-bg-tertiary text-gray-900 dark:text-text-primary placeholder:text-gray-400 dark:placeholder:text-text-tertiary/40 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                            {/* Top tier — derived from the green cutoff, read-only */}
                            <div className="flex items-center gap-2">
                              <span className={cn('inline-block w-2 h-2 rounded-full shrink-0', TIER_DOT_CLASS.deep)} />
                              <span className="text-xs capitalize text-gray-700 dark:text-text-secondary">green</span>
                              <span className="ml-auto text-[11px] tabular-nums text-muted-foreground dark:text-text-tertiary">${liqGreen} and up</span>
                            </div>
                          </div>
                          {liquidityError && (
                            <p className="text-[11px] text-red-600 dark:text-red-400">{liquidityError}</p>
                          )}
                        </div>
                      )}

                      {onMinBestLineLiquidityChange && minBestLineLiquidity !== undefined && (
                        <div className="space-y-1.5 pt-2 border-t border-gray-200 dark:border-border-primary/60">
                          <div className="flex items-center gap-2">
                            <label htmlFor="best-line-min-liquidity" className="text-[11px] font-medium text-muted-foreground dark:text-text-tertiary uppercase tracking-wider">
                              Best Line Min Liquidity
                            </label>
                            <div className="relative ml-auto">
                              <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground dark:text-text-tertiary/60 pointer-events-none">$</span>
                              <input
                                id="best-line-min-liquidity"
                                type="number"
                                min={0}
                                step={1}
                                placeholder="0"
                                value={minBestLineLiquidity === 0 ? '' : minBestLineLiquidity}
                                onChange={(e) => {
                                  const v = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
                                  onMinBestLineLiquidityChange(Number.isNaN(v) ? 0 : Math.max(0, v));
                                }}
                                className="block w-[4.5rem] pl-4 pr-1.5 py-1 text-xs tabular-nums rounded-md border border-gray-200 dark:border-border-primary bg-white dark:bg-bg-tertiary text-gray-900 dark:text-text-primary placeholder:text-gray-400 dark:placeholder:text-text-tertiary/40 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                              />
                            </div>
                          </div>
                          <p className="text-[10px] text-muted-foreground dark:text-text-tertiary">
                            Exchange prices with less liquidity still show in their column but never count as the best line.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {download}
            </div>

            {/* Teams + Markets. NBA: when both disclosures are closed the headers
                sit side-by-side to save vertical space; opening either reverts to
                stacking. MLB always stacks — Markets is two rows (Pitcher/Batter).
                The MLB confirmed-lineup toggle rides in the Teams header. */}
            <div
              ref={filterChipsRef}
              data-tour="tour-filters"
              className={cn('pt-2', sideBySide ? 'flex items-center gap-4' : 'space-y-1')}
            >
              <div className={sideBySide ? 'min-w-0' : undefined}>
                <TeamFilterLocal
                  selectedTeams={selectedTeams}
                  onTeamsChange={onTeamsChange}
                  availableTeams={availableTeams}
                  teamsWithQuestionable={teamsWithQuestionable}
                  isCollapsed={false}
                  expanded={teamsExpanded}
                  onExpandedChange={setTeamsExpanded}
                  confirmedLineupsOnly={confirmedLineupsOnly}
                  onConfirmedLineupsOnlyChange={onConfirmedLineupsOnlyChange}
                />
              </div>

              <div className={sideBySide ? 'min-w-0' : undefined}>
                <MarketFilterLocal
                  selectedMarkets={selectedMarkets}
                  onMarketsChange={onMarketsChange}
                  availableMarkets={availableMarkets}
                  isCollapsed={false}
                  expanded={marketsExpanded}
                  onExpandedChange={setMarketsExpanded}
                />
              </div>
            </div>

            {/* Questionable filter */}
            {outQuestionable && onToggleOut && (
              <QuestionableFilter players={shownQuestionable} out={outQuestionable} onToggle={onToggleOut} onClearAll={onClearOut} isCollapsed={false} className="pt-1" />
            )}

            {/* Timestamp + Selection Actions. Timestamp shows on all breakpoints
                (its own row on mobile, where it used to ride the markets header);
                the Select all / Clear actions are desktop-only — mobile has no
                selection model, so gating them avoids a stray "Select all". */}
            <div className="pt-2 flex justify-between items-center gap-2">
              {(lastUpdated || projectionsHidden) && (
                <div className="flex items-center gap-1.5">
                  <TimestampIcon className={cn('w-3.5 h-3.5', timestampIconColor)} />
                  <span className={cn('text-xs font-medium', timestampTextColor)}>
                    {projectionsTimestampText}
                  </span>
                </div>
              )}
              {/* ml-auto keeps the actions right-aligned even when the timestamp
                  sibling is absent (e.g. signed out: no projection timestamp). */}
              <div className="hidden md:flex items-center gap-2 ml-auto">
                {filteredPlayers.length > 0 && !filteredPlayers.every(p => selectedIds.has(p.id)) && (
                  <button
                    onClick={() => onSelectAll(filteredPlayers.map(p => p.id))}
                    className="text-xs text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 font-medium"
                  >
                    Select all
                  </button>
                )}
                {selectedIds.size > 0 && (
                  <button
                    onClick={onClearAll}
                    className="text-xs text-gray-400 dark:text-text-tertiary hover:text-gray-700 dark:hover:text-text-secondary flex items-center gap-1"
                  >
                    <X className="w-3 h-3" />
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Player List */}
          <div className="flex-1 flex flex-col min-h-0 group">
          <div
            ref={parentRef}
            className="flex-1 overflow-auto scrollbar-on-group-hover px-1 sm:px-2 py-1.5 sm:py-2"
          >
            {filteredPlayers.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 dark:text-text-tertiary">
                <Search className="w-12 h-12 mb-2 text-gray-300 dark:text-text-quaternary" />
                <p className="text-sm">No players found</p>
              </div>
            ) : (
              // Virtualized list for all datasets
              <div
                style={{
                  height: `${rowVirtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative',
                }}
              >
                {rowVirtualizer.getVirtualItems().map((virtualItem) => {
                  const player = filteredPlayers[virtualItem.index];
                  return (
                    <div
                      key={`${player.id}:${player.game?.id ?? 'g'}`}
                      data-index={virtualItem.index}
                      data-card-id={player.id}
                      ref={rowVirtualizer.measureElement}
                      className={cardClassName}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${virtualItem.start}px)`,
                      }}
                    >
                      <div className="border-b border-border/40 dark:border-border-primary/30">
                        <SelectablePlayerCard
                          player={player}
                          isSelected={selectedIds.has(player.id)}
                          onToggle={onTogglePlayer}
                          expandedCardId={expandedCardId}
                          onToggleExpand={handleToggleExpand}
                          selectedSportsbooks={selectedSportsbooks}
                          selectedMarkets={selectedMarkets}
                          isQuestionable={player.questionable}
                          onOpenResearch={onOpenResearch}
                          onLineClick={onLineMovementClick}
                          notesKey={cardKey(player)}
                          onNotesOpenChange={onAnchorChange}
                          weather={player.game ? getWeather(
                            player.game.isHome ? player.team : player.game.opp,
                            player.game.commenceTime,
                          ) : null}
                          tourTargetIds={(() => {
                            const isFirstRow = virtualItem.index === 0;
                            const isDemoPlayer = player.id === demoPlayerId;
                            const isLineupDemo = player.id === demoLineupPlayerId;
                            const isWeatherDemo = player.id === demoWeatherPlayerId;
                            if (!isFirstRow && !isDemoPlayer && !isLineupDemo && !isWeatherDemo) return undefined;
                            return {
                              ...(isFirstRow
                                ? { playerName: 'tour-demo-player-name', cardRoot: 'tour-demo-line-row' }
                                : {}),
                              ...(isDemoPlayer ? { firstBookButton: 'tour-demo-first-book' } : {}),
                              ...(isLineupDemo ? { lineupButton: 'tour-demo-lineup' } : {}),
                              ...(isWeatherDemo ? { weatherButton: 'tour-demo-weather' } : {}),
                            };
                          })()}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          </div>
        </div>
      )}

    </div>
  );
}